import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { batchTranslateText } from "@/lib/gemini/client";
import { useUserStore } from "@/store/userStore";
import { cn } from "@/lib/utils";
import { Menu } from "@/types/menu";
import { getLanguageInfo } from "@/lib/languages";
import { Link } from "@/i18n/navigation";
import * as flags from "country-flag-icons/react/3x2";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/mixpanel-client";

type MenuTitleEditDialogProps = {
  menu: Menu;
  language: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedMenu: Menu) => Promise<void>;
  supportedLanguages?: string[];
};

export default function MenuTitleEditDialog({
  menu,
  language,
  isOpen,
  onClose,
  onUpdate,
  supportedLanguages = [],
}: MenuTitleEditDialogProps) {
  const { toast } = useToast();
  const { getRemainingCredits } = useUserStore();
  const remainingCredits = getRemainingCredits();
  const t = useTranslations("MenuTitleEditDialog");
  const tLang = useTranslations("LanguageNames");

  const [title, setTitle] = useState<Record<string, string>>(menu.title || {});
  const [inputValue, setInputValue] = useState<string>(
    (menu.title && menu.title[language]) || ""
  );
  const [descriptionValue, setDescriptionValue] = useState<string>(
    (menu.description && menu.description[language]) || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [shouldTranslate, setShouldTranslate] = useState(remainingCredits > 0);
  const [initialShouldTranslate] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(() => {
    if (remainingCredits > 0) {
      const availableLanguages = supportedLanguages.filter(
        (lang) => lang !== language
      );
      return availableLanguages.slice(0, remainingCredits);
    }
    return [];
  });
  const [initialSelectedLanguages] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showCancelTranslationAlert, setShowCancelTranslationAlert] =
    useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Calculate required credits for translations
  const requiredCredits = supportedLanguages.length - 1; // Exclude current language

  // Helper function to get country code for a language
  const getCountryCode = (languageCode: string): string => {
    // Special cases for Mandarin variants
    if (
      languageCode.toLowerCase() === "chinese_simplified" ||
      languageCode.toLowerCase() === "simplified_chinese" ||
      languageCode.toLowerCase() === "chinese-simplified"
    ) {
      return "CN"; // China flag for Simplified Chinese
    }
    if (
      languageCode.toLowerCase() === "chinese_traditional" ||
      languageCode.toLowerCase() === "traditional_chinese" ||
      languageCode.toLowerCase() === "chinese-traditional"
    ) {
      return "TW"; // Taiwan flag for Traditional Chinese
    }

    const countryMap: Record<string, string> = {
      arabic: "SA",
      bhojpuri: "IN",
      cantonese: "HK",
      chinese: "CN", // Generic Chinese
      chinese_simplified: "CN",
      chinese_traditional: "TW",
      czech: "CZ",
      dutch: "NL",
      english: "GB",
      filipino: "PH",
      french: "FR",
      german: "DE",
      hebrew: "IL",
      hindi: "IN",
      indonesian: "ID",
      italian: "IT",
      japanese: "JP",
      korean: "KR",
      malay: "MY",
      mandarin: "CN", // Generic Mandarin
      mandarin_simplified: "CN",
      mandarin_traditional: "TW",
      persian: "IR",
      polish: "PL",
      portuguese: "PT",
      portuguese_brasil: "BR",
      portuguese_brasileiro: "BR",
      português_brasil: "BR",
      português_brasileiro: "BR",
      romanian: "RO",
      russian: "RU",
      simplified_chinese: "CN",
      slovenian: "SI",
      spanish: "ES",
      swedish: "SE",
      thai: "TH",
      traditional_chinese: "TW",
      turkish: "TR",
      vietnamese: "VN",
    };

    // Special check for Brazilian Portuguese variants
    if (
      languageCode.toLowerCase().includes("portuguese") ||
      languageCode.toLowerCase().includes("português")
    ) {
      if (
        languageCode.toLowerCase().includes("brasil") ||
        languageCode.toLowerCase().includes("brazil") ||
        languageCode.toLowerCase().includes("brasileiro")
      ) {
        return "BR";
      }
    }

    return countryMap[languageCode.toLowerCase()] || "US"; // Default to US flag
  };

  // Get localized language name
  const getLocalizedLanguageName = (code: string): string => {
    // Special cases for languages with variants
    if (code === "chinese-simplified" || code === "chinese_simplified") {
      return tLang("chinese-simplified");
    }
    if (code === "chinese-traditional" || code === "chinese_traditional") {
      return tLang("chinese-traditional");
    }

    // Try to get translation from the LanguageNames namespace
    try {
      return tLang(code.toLowerCase());
    } catch {
      // Fallback to the language name from getLanguageInfo if translation is missing
      return getLanguageInfo(code)?.label || code;
    }
  };

  // Helper function to check if current state is different from initial state
  const checkForChanges = (
    newTitleValue: string,
    newDescriptionValue: string
  ) => {
    const hasTitleChange = newTitleValue !== (menu.title?.[language] || "");
    const hasDescriptionChange =
      newDescriptionValue !== (menu.description?.[language] || "");

    // Consider translation state changes
    const currentTranslationState =
      shouldTranslate && selectedLanguages.length > 0;
    const initialTranslationState =
      initialShouldTranslate && initialSelectedLanguages.length > 0;
    const hasTranslateChange =
      currentTranslationState !== initialTranslationState;

    // Only consider language selection changes if translations are enabled
    const hasLanguageSelectionChange =
      currentTranslationState &&
      (!initialTranslationState ||
        !arraysHaveSameElements(selectedLanguages, initialSelectedLanguages));

    return (
      hasTitleChange ||
      hasDescriptionChange ||
      hasTranslateChange ||
      hasLanguageSelectionChange
    );
  };

  // Helper function to compare arrays regardless of order
  const arraysHaveSameElements = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    const set1 = new Set(arr1);
    return arr2.every((item) => set1.has(item));
  };

  // Update title when the menu prop changes
  useEffect(() => {
    // Don't reset the state if we're in the middle of saving or translating
    // This prevents the input from reverting during translation
    if (isSaving || isTranslating) {
      return;
    }

    setTitle(menu.title || {});
    setInputValue((menu.title && menu.title[language]) || "");
    setDescriptionValue((menu.description && menu.description[language]) || "");

    // Reset translation states based on available credits
    if (remainingCredits > 0) {
      setShouldTranslate(true);
      const availableLanguages = supportedLanguages.filter(
        (lang) => lang !== language
      );
      setSelectedLanguages(availableLanguages.slice(0, remainingCredits));
    } else {
      setShouldTranslate(false);
      setSelectedLanguages([]);
    }

    setHasChanges(false);
  }, [
    menu,
    language,
    supportedLanguages,
    remainingCredits,
    isSaving,
    isTranslating,
  ]);

  // Update the title change handler
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;
    setInputValue(newInputValue);

    // Also update the title object
    const newTitle = {
      ...title,
      [language]: newInputValue,
    };
    setTitle(newTitle);

    setHasChanges(checkForChanges(newInputValue, descriptionValue));
  };

  // Add description change handler
  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newDescriptionValue = e.target.value;
    setDescriptionValue(newDescriptionValue);
    setHasChanges(checkForChanges(inputValue, newDescriptionValue));
  };

  const handleTranslateToggle = (checked: boolean) => {
    if (checked && remainingCredits === 0) {
      toast({
        variant: "destructive",
        title: "No Credits Available",
        description:
          "You need translation credits to update translations. You can upgrade your plan for more monthly credits or purchase additional credits.",
      });
      return;
    }
    setShouldTranslate(checked);
    if (checked) {
      // When enabling translation, only select languages up to the remaining credits
      const availableLanguages = supportedLanguages.filter(
        (lang) => lang !== language
      );
      const languagesToSelect = availableLanguages.slice(0, remainingCredits);
      setSelectedLanguages(languagesToSelect);
      setHasChanges(checkForChanges(inputValue, descriptionValue));

      // Show a warning if there are more languages available than credits
      if (availableLanguages.length > remainingCredits) {
        toast({
          variant: "default",
          title: "Limited Credits Available",
          description: `You can select up to ${remainingCredits} language${
            remainingCredits === 1 ? "" : "s"
          } with your remaining credits.`,
        });
      }
    } else {
      // When disabling translation, clear selected languages
      setSelectedLanguages([]);
      setHasChanges(checkForChanges(inputValue, descriptionValue));
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    let translationAttempted = false;
    let translationSucceeded = true; // Assume success

    // Create a new AbortController for this save operation
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const updatedMenuData = JSON.parse(JSON.stringify(menu)) as Menu;
      const hasTitleChanged = inputValue !== (menu.title?.[language] || "");
      const hasDescriptionChanged =
        descriptionValue !== (menu.description?.[language] || "");

      if (hasTitleChanged) {
        updatedMenuData.title[language] = inputValue;
      }
      if (hasDescriptionChanged) {
        updatedMenuData.description = {
          ...(updatedMenuData.description || {}),
          [language]: descriptionValue,
        };
      } else if (!descriptionValue && updatedMenuData.description) {
        // Handle case where description is cleared in current language
        delete updatedMenuData.description[language];
      }

      // Prepare properties for tracking
      const updatedFields = [];
      if (hasTitleChanged) updatedFields.push("Title");
      if (hasDescriptionChanged) updatedFields.push("Description");

      // Track menu title/description update
      if (updatedFields.length > 0) {
        trackEvent("Menu Details Updated", {
          "Updated Fields": updatedFields,
          Language: language,
        });
      }

      if (
        supportedLanguages.length > 0 &&
        shouldTranslate &&
        selectedLanguages.length > 0 &&
        remainingCredits > 0 &&
        (hasTitleChanged || hasDescriptionChanged)
      ) {
        translationAttempted = true;
        setIsTranslating(true);
        const textsToTranslate = [];
        if (hasTitleChanged && inputValue.trim()) {
          textsToTranslate.push({ text: inputValue, type: "title" as const });
        }
        if (hasDescriptionChanged && descriptionValue.trim()) {
          textsToTranslate.push({
            text: descriptionValue,
            type: "menu_description" as const,
          });
        }

        if (textsToTranslate.length > 0) {
          try {
            // Track translation attempt
            trackEvent("Menu Translation Started", {
              "Source Language": language,
              "Target Languages": selectedLanguages,
              "Content Types": textsToTranslate.map((t) => t.type),
            });

            // Use the batch translation function
            const translationResults = await batchTranslateText(
              textsToTranslate,
              language,
              selectedLanguages
              // TODO: Pass signal if batchTranslateText supports it
            );

            if (signal.aborted) throw new Error("Translation aborted");

            // Apply translations
            for (const targetLang of selectedLanguages) {
              if (translationResults[targetLang]) {
                if (
                  hasTitleChanged &&
                  textsToTranslate.some((t) => t.type === "title")
                ) {
                  const titleIndex = textsToTranslate.findIndex(
                    (t) => t.type === "title"
                  );
                  const translatedTitle =
                    translationResults[targetLang][`text${titleIndex + 1}`];
                  if (translatedTitle?.trim())
                    updatedMenuData.title[targetLang] = translatedTitle;
                }
                if (
                  hasDescriptionChanged &&
                  textsToTranslate.some((t) => t.type === "menu_description")
                ) {
                  const descIndex = textsToTranslate.findIndex(
                    (t) => t.type === "menu_description"
                  );
                  const translatedDesc =
                    translationResults[targetLang][`text${descIndex + 1}`];
                  if (translatedDesc?.trim()) {
                    updatedMenuData.description = {
                      ...(updatedMenuData.description || {}),
                      [targetLang]: translatedDesc,
                    };
                  } else if (updatedMenuData.description) {
                    // If translation is empty, remove the key for that language
                    delete updatedMenuData.description[targetLang];
                  }
                }
              }
            }

            // Track successful translation
            trackEvent("Menu Translation Completed", {
              "Source Language": language,
              "Target Languages": selectedLanguages,
              "Content Types": textsToTranslate.map((t) => t.type),
            });

            // Track credits consumed
            trackEvent("Credits Consumed", {
              "Credits Used": selectedLanguages.length,
              Feature: "Menu Translation",
              Languages: selectedLanguages,
            });
          } catch (error: unknown) {
            translationSucceeded = false;
            if (!signal?.aborted) {
              console.error("Batch translation error:", error);
              const errorMessage =
                error instanceof Error
                  ? error.message
                  : "Unknown translation error";
              toast({
                variant: "destructive",
                title: "Batch Translation Error",
                description: `Failed to translate to some languages. ${errorMessage}`,
              });

              // Track translation failure
              trackEvent("Menu Translation Failed", {
                "Source Language": language,
                "Target Languages": selectedLanguages,
                Error: errorMessage,
              });
            }
          } finally {
            setIsTranslating(false);
            abortControllerRef.current = null;
          }
        } else {
          // No text changed that needed translation
          translationAttempted = false;
        }
      } // End if shouldTranslate

      if (signal?.aborted) {
        setIsSaving(false);
        return;
      }

      // Filter out empty fields before final update
      const filteredTitle = Object.fromEntries(
        Object.entries(updatedMenuData.title).filter(([, text]) => text?.trim())
      );
      const filteredDescription = updatedMenuData.description
        ? Object.fromEntries(
            Object.entries(updatedMenuData.description).filter(([, text]) =>
              text?.trim()
            )
          )
        : undefined;

      updatedMenuData.title = filteredTitle;
      updatedMenuData.description =
        filteredDescription && Object.keys(filteredDescription).length > 0
          ? filteredDescription
          : undefined;

      // Call the actual update function passed via props
      await onUpdate(updatedMenuData);
      onClose(); // Close dialog on success

      // Show appropriate toast
      if (translationAttempted) {
        if (translationSucceeded) {
          toast({
            title: "Menu Details Updated & Translated",
            description:
              "Your changes have been saved and translated successfully.",
          });
        } else {
          toast({
            title: "Menu Details Updated (Partial Translation)",
            description:
              "Changes saved, but some translations failed. Please check and retry if needed.",
            variant: "default",
          });
        }
      } else {
        toast({
          title: "Menu Details Updated",
          description: "Your changes have been saved successfully.",
        });
      }
    } catch (error: unknown) {
      // Catch errors from onUpdate or other issues
      if (!signal?.aborted) {
        // Don't show error if intentionally aborted
        console.error("Error saving menu details:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to save changes.";
        toast({
          variant: "destructive",
          title: "Save Error",
          description: `${errorMessage} Please try again.`,
        });
      }
    } finally {
      setIsSaving(false);
      setIsTranslating(false); // Ensure translating state is reset
      abortControllerRef.current = null;
    }
  };

  // Handle translation cancellation
  const handleCancelTranslation = () => {
    // Abort the translation if in progress
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsTranslating(false);
    setShowCancelTranslationAlert(false);
    onClose();

    toast({
      title: "Translation Cancelled",
      description: "The translation process has been cancelled.",
    });
  };

  // Update the dialog open change handler
  const handleDialogOpenChange = (open: boolean) => {
    if (!open && isTranslating) {
      // If trying to close while translating, show confirmation dialog
      setShowCancelTranslationAlert(true);
      return;
    }

    if (!open) {
      onClose();
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="p-0 sm:p-6 h-[100dvh] w-[100dvw] sm:h-auto sm:w-auto sm:min-w-[500px] sm:max-w-[600px] sm:max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0 p-6 pb-4 sm:p-0 sm:pb-2">
            <DialogTitle>{t("title")}</DialogTitle>
            <DialogDescription className="text-pretty">
              {t("description")}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-grow px-6 py-4 sm:px-1">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="title">{t("menuTitle")}</Label>
                <Input
                  id="title"
                  value={inputValue}
                  onChange={handleTitleChange}
                  placeholder={
                    language === "japanese"
                      ? "メニュータイトル"
                      : t("menuTitlePlaceholder")
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">{t("menuDescription")}</Label>
                <Textarea
                  id="description"
                  value={descriptionValue}
                  onChange={handleDescriptionChange}
                  placeholder={t("menuDescriptionPlaceholder")}
                  className="resize-y"
                  rows={7}
                />
              </div>

              {supportedLanguages.length > 1 && (
                <div className="space-y-4 pt-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="translate"
                      checked={shouldTranslate}
                      onCheckedChange={handleTranslateToggle}
                      disabled={remainingCredits === 0}
                    />
                    <Label
                      htmlFor="translate"
                      className={cn(
                        remainingCredits === 0 ? "text-muted-foreground" : "",
                        "flex-1"
                      )}
                    >
                      {t("translateOtherLanguages")}
                    </Label>
                    {remainingCredits > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {remainingCredits}{" "}
                        {remainingCredits === 1 ? t("credit") : t("credits")}
                      </span>
                    )}
                  </div>

                  {remainingCredits === 0 && (
                    <div className="text-sm text-destructive">
                      {t("noCreditsAvailable")}{" "}
                      <Link
                        href="/dashboard/settings/plan#purchase-credits"
                        className="underline"
                      >
                        {t("purchaseCredits")}
                      </Link>
                    </div>
                  )}

                  {remainingCredits > 0 &&
                    remainingCredits < requiredCredits && (
                      <div className="text-sm text-destructive">
                        {t("notEnoughCredits", { requiredCredits })}{" "}
                        <Link
                          href="/dashboard/settings/plan#purchase-credits"
                          className="underline"
                        >
                          {t("purchaseMoreCredits")}
                        </Link>
                      </div>
                    )}

                  {shouldTranslate && remainingCredits > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>{t("selectLanguages")}</Label>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="select-all-languages"
                            checked={
                              selectedLanguages.length ===
                              supportedLanguages.filter(
                                (lang) => lang !== language
                              ).length
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                const availableLanguages =
                                  supportedLanguages.filter(
                                    (lang) => lang !== language
                                  );
                                const languagesToSelect =
                                  availableLanguages.slice(
                                    0,
                                    Math.min(
                                      availableLanguages.length,
                                      remainingCredits
                                    )
                                  );
                                setSelectedLanguages(languagesToSelect);
                                setHasChanges(
                                  checkForChanges(inputValue, descriptionValue)
                                );

                                if (
                                  availableLanguages.length > remainingCredits
                                ) {
                                  toast({
                                    variant: "default",
                                    title: t("limitedCreditsTitle"),
                                    description: t(
                                      "limitedCreditsDescription",
                                      {
                                        count: remainingCredits,
                                        plural:
                                          remainingCredits === 1 ? "" : "s",
                                      }
                                    ),
                                  });
                                }
                              } else {
                                setSelectedLanguages([]);
                                setHasChanges(
                                  checkForChanges(inputValue, descriptionValue)
                                );
                              }
                            }}
                          />
                          <Label
                            htmlFor="select-all-languages"
                            className="text-sm"
                          >
                            {t("selectAll")}
                          </Label>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {supportedLanguages.map((lang) => {
                          if (lang === language) return null;
                          const langInfo = getLanguageInfo(lang);
                          const countryCode = getCountryCode(lang);
                          const FlagComponent =
                            flags[countryCode as keyof typeof flags];
                          const isDisabled =
                            !selectedLanguages.includes(lang) &&
                            selectedLanguages.length >= remainingCredits;

                          return (
                            <div
                              key={lang}
                              className={cn(
                                "flex items-center p-2 px-4 bg-muted/50 rounded-md",
                                isDisabled && "opacity-50"
                              )}
                            >
                              <Checkbox
                                id={`lang-${lang}`}
                                checked={selectedLanguages.includes(lang)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    if (
                                      selectedLanguages.length + 1 >
                                      remainingCredits
                                    ) {
                                      toast({
                                        variant: "destructive",
                                        title: t("insufficientCredits"),
                                        description: t(
                                          "notEnoughCreditsMessage"
                                        ),
                                      });
                                      return;
                                    }
                                    const newSelectedLanguages = [
                                      ...selectedLanguages,
                                      lang,
                                    ];
                                    setSelectedLanguages(newSelectedLanguages);
                                    setHasChanges(
                                      checkForChanges(
                                        inputValue,
                                        descriptionValue
                                      )
                                    );
                                  } else {
                                    const newSelectedLanguages =
                                      selectedLanguages.filter(
                                        (l) => l !== lang
                                      );
                                    setSelectedLanguages(newSelectedLanguages);
                                    setHasChanges(
                                      checkForChanges(
                                        inputValue,
                                        descriptionValue
                                      )
                                    );
                                  }
                                }}
                                disabled={isDisabled}
                                className="mr-2"
                              />
                              <div className="flex-shrink-0 mx-2">
                                {FlagComponent && (
                                  <FlagComponent className="w-6 h-4 rounded shadow-sm" />
                                )}
                              </div>
                              <div className="flex flex-col flex-1 ml-2">
                                <Label
                                  htmlFor={`lang-${lang}`}
                                  className={cn(
                                    "text-sm font-medium",
                                    isDisabled && "text-muted-foreground"
                                  )}
                                >
                                  {langInfo?.nativeName}
                                </Label>
                                <span className="text-xs text-muted-foreground">
                                  {getLocalizedLanguageName(lang)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 p-6 pt-4 sm:p-0 mt-auto border-t sm:border-0">
            <div className="flex gap-2 w-full sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (isTranslating) {
                    setShowCancelTranslationAlert(true);
                  } else {
                    onClose();
                  }
                }}
                className="flex-1 sm:flex-none"
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className="flex-1 sm:flex-none"
              >
                {isSaving ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                    <span>
                      {isTranslating ? t("translating") : t("updating")}
                    </span>
                  </div>
                ) : (
                  t("update")
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Translation Alert Dialog */}
      {showCancelTranslationAlert && (
        <Dialog
          open={showCancelTranslationAlert}
          onOpenChange={(open) => !open && setShowCancelTranslationAlert(false)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Translation?</DialogTitle>
              <DialogDescription>
                Translation is in progress. Are you sure you want to cancel?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCancelTranslationAlert(false)}
              >
                Continue Translation
              </Button>
              <Button onClick={handleCancelTranslation}>
                Cancel Translation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
